<template>
  <transition
      name="slide"
      @after-enter="$emit( 'opened' )"
      @enter-cancelled="$emit( 'opened' )"
      @after-leave="$emit( 'closed' )"
      @leave-cancelled="$emit( 'closed' )"
  >
    <div class="tooltip" :class="classes" v-if="show_" :key="$options.id" ref="main">
      <span :class="{ content: true, wrapNo: !wrap, pad }" v-resize="{ arg: resize }">
        <span v-if="content" v-html="content" />
        <slot></slot>
        <Icon
          symbol="close"
          class="closeIconSymbol"
          size="1.2em"
          @click.native="close"
        />
      </span>
    </div>
  </transition>
</template>

<script>
/*
ORIENTATION:

 TOP
 [SOMETHING]

 [SOMETHING]
 BOTTOM

 [SOMETHING] RIGHT

 LEFT [SOMETHING]

ANGLEPOSITION:

  -^--- START
  --^-- MIDDLE
  ---^- END
*/
	import utils from "@/utils";
  import Icon from "./Icon";

  export default {
		name:  "Tooltip",

    components: {
		  Icon
    },

		props: {
		  show: {
		    type: Boolean|null,
        default: null
      },
      orientation: {
        validator( v ) {
          return [ "top", "bottom", "left", "right" ].includes( v )
        },
        default: "bottom"
      },
      anglePosition: {
        validator( v ) {
          return [ "start", "middle", "end" ].includes( v )
        },
        default: "start"
      },
      type: {
        validator( v ) {
          return [ "normal", "error", "block" ].includes( v )
        },
        default: "normal"
      },
      closeIcon: {
		    type: Boolean,
        default: true
      },
      content: {
		    type: String,
        default: null
      },

      //resize by mouse
      resize: {
		    type: String,
        default: "none",
        validate( v ) {
		      return [ "none", "vertical", "horizontal", "both" ].includes( v );
        }
      },
      debounce: {
		    type: Boolean,
        default: false
      },

      //false means white-space: nowrap
      wrap: {
		    type: Boolean,
        default: false
      },

      //false means pointer-events: none
      interaction: {
		    type: Boolean,
        default: true
      },

      //true means special non-visible pad at top, used for not loosing mouse events when mouse moves from parent to children tooltip
      pad: {
		    type: Boolean,
        default: false
      }
		},

    id: 'tt_' + utils.uniqueHashGen(),

		data() {
			return {
			  show_: false
			};
		},
    created() {
      this.showCheck();
    },
    beforeUpdate() {
      this.showCheck();
    },
    methods: {
      showCheck() {
        let show;

        if ( this.show !== null ) show = !!this.show;
        else if ( this.content !== null ) show = !!this.content;
        else show = this.$slots.default && this.$slots.default.reduce(
            ( hasContent, node ) => ( hasContent || node.tag || ( node.text && node.text.trim() ) ),
            false
        );

        if ( this.debounce ) {
          if ( this.$options.tid ) {
            if ( this.$options.tv === show ) return;

            clearTimeout( this.$options.tid );
          }

          this.$options.tv = show;
          this.$options.tid = setTimeout(
            () => {
              this.show_ = this.$options.tv;
              delete this.$options.tid;
              delete this.$options.tv;
            },
            10
          );
        }
        else this.show_ = show;
      },
      close() {
        this.$emit( "close" );
      }
    },
    computed: {
		  classes() {
		    return this.orientation +
            " angle" + this.anglePosition.charAt( 0 ).toUpperCase() + this.anglePosition.slice( 1 )  + " " +
            this.type + " " +
            ( this.closeIcon ? "closeIcon" : "" ) +
            ( this.interaction ? "" : "interactionNo" );
      }
    },
    watch: {
		  content() {
		    this.showCheck();
      },
      show_: {
		    immediate: true,
        handler() {
          if ( this.show_ ) {
            if ( !this.$options.closeHandler ) {
              this.$options.closeHandler = e => {
                if ( this.$refs.main && !this.$refs.main.contains( e.target ) ) this.close();
              };

              document.addEventListener( "mousedown", this.$options.closeHandler );
              document.addEventListener( "keydown", this.$options.closeHandler );
            }
          }
          else {
            if ( this.$options.closeHandler ) {
              document.removeEventListener( "mousedown", this.$options.closeHandler );
              document.removeEventListener( "keydown", this.$options.closeHandler );
              delete this.$options.closeHandler;
            }
          }
        }
      }
    }
	};
</script>

<style scoped>
.tooltip {
  position: relative;
  z-index: 5000;
  font-size: 0.8em;
  line-height: 0;
  width: 0;
}

.tooltip::before,
.tooltip::after {
  content: "";
  width: 1em;
  height: 1em;
  position: absolute;
  z-index: 1;
  font-size: .9em;
  line-height: 1;
  text-transform: none;
  user-select: none;
  pointer-events: none;
  top: 0;
  left: 0.5em;
}

.tooltip::after {
  clip-path: polygon( 0 0, 100% 0, 100% 50%, 0 50% );
}

.tooltip::before {
  z-index: -1;
  background-color: #333333;
  box-shadow: 0 0 0 1px rgba( 0, 0, 0, 0.5 ), 0 0.2em 0.2em 0 rgba( 0, 0, 0, 0.35 );
}

.tooltip.bottom::before,
.tooltip.bottom:after {
  transform: rotate( 45deg ) translate( 0.8em, 0.5em );
}

.tooltip.bottom.angleEnd::before,
.tooltip.bottom.angleEnd:after {
  left: unset;
  right: 0.5em;
}

.tooltip.interactionNo {
  pointer-events: none;
}

.tooltip > .content {
  position: absolute;
  min-width: 3em;
  max-width: 21em;
  padding: 0.5em;
  border-radius: 0.2em;
  box-shadow: 0 0 0 1px rgba( 0, 0, 0, 0.5 ), 0 0.2em 0.2em 0 rgba( 0, 0, 0, 0.35 );
  background-color: #333333;
  color: #FFFFFF;
  z-index: 1;
  top: 0; /*100%;*/
  left: 0;
  margin-top: 1em;
}

.tooltip > .content.pad:before {
  content: "";
  position: absolute;
  top: -1.5em;
  left: 0;
  right: 0;
  height: 2em;
  opacity: 0;
  z-index: 1;
}

.tooltip > .content.wrapNo {
  white-space: nowrap;
  max-width: none;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 300ms ease-out;
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  transform: translateY( 0.5em );
}

.slide-enter-to,
.slide-leave {
  opacity: 1;
  transform: translateY( 0 );
}


/*
@keyframes tooltipsVert {
  from {
    opacity: 0;
    transform: translateY( 0.5em );
  }

  to {
    opacity: 1;
    transform: translateY( 0 );
  }
}

.tooltip {
  animation: tooltipsVert 3000ms ease-out forwards;
}
*/

.tooltip > .content > .closeIconSymbol {
  display: none;
  position: absolute;
  right: 0;
  top: -0.2em;
  cursor: pointer;
  margin: 0.2em;
}
.tooltip > .content > .closeIconSymbol:hover {
  fill: var(--controlActiveColor) !important;
}
.tooltip.closeIcon > .content {
  padding-top: 1.2em;
  padding-right: 1.2em;
}

.tooltip.closeIcon > .content > .closeIconSymbol {
  display: block;
}

.tooltip.error::after {
  background-color: #E87060;
}

.tooltip.error > .content {
  background-color: #E87060;
  color: #FFFFFF;
}


.tooltip.block:before,
.tooltip.block:after,
.tooltip.block > .content {
  background-color: #FFFFFF;
  color: #000000;
}
</style>
